
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { format, getDay } from 'date-fns';
import { TimeSlot, TimeSlotGroup, Doctor } from '../../types';
import TimeSlotItem from './TimeSlotItem';

interface TimeSlotsSectionProps {
  date: Date | null;
  doctor: Doctor | null;
  loading: boolean;
  apiError: boolean;
  timeSlotGroups: TimeSlotGroup[];
  selectedTimeSlot: TimeSlot | null;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
}

const TimeSlotsSection: React.FC<TimeSlotsSectionProps> = ({
  date,
  doctor,
  loading,
  apiError,
  timeSlotGroups,
  selectedTimeSlot,
  onTimeSlotSelect,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg text-teal-700">
          {date 
            ? `Available Times for ${format(date, 'MMMM do, yyyy')}` 
            : 'Please select a date first'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-6">
            {[...Array(3)].map((_, i) => (
              <div key={i}>
                <Skeleton className="h-5 w-24 mb-3" />
                <div className="grid grid-cols-3 gap-2">
                  {[...Array(6)].map((_, j) => (
                    <Skeleton key={j} className="h-8 w-full rounded" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : apiError ? (
          <div className="text-center py-6">
            <p className="text-red-500 mb-2">Unable to check availability.</p>
            <p className="text-muted-foreground">All time slots are shown as available.</p>
          </div>
        ) : date ? (
          timeSlotGroups.length > 0 ? (
            <div className="space-y-6">
              {timeSlotGroups.map((group) => (
                <div key={group.label}>
                  <h3 className="text-sm font-medium text-teal-600 mb-2">{group.label}</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {group.slots.map((slot) => (
                      <TimeSlotItem
                        key={slot.id}
                        slot={slot}
                        isSelected={selectedTimeSlot?.id === slot.id}
                        onClick={() => onTimeSlotSelect(slot)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground">
              {doctor?.weeklyOffDays?.includes(getDay(date)) 
                ? `Dr. ${doctor.name} does not have appointments on this day.`
                : "No time slots available for this date"}
            </div>
          )
        ) : (
          <div className="text-center py-6 text-muted-foreground">
            Please select a date to view available time slots
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TimeSlotsSection;
